import React from 'react'

import { SendReferralTutorial } from 'src/features/business/referrals/components/send-referral-tutorial'
import { useSendReferralStore } from 'src/features/business/referrals/stores/send-referral-store'
import { useAddPoints } from 'src/shared/api/points/use-add-points'
import { ErrorBoundaryWrapper } from 'src/shared/components/errors/error-boundary-wrapper'
import { PointIcon } from 'src/shared/components/icons/point-icon'
import { usePointsToast } from 'src/shared/components/ui/popups/toast/points-toast'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { useToast } from 'src/shared/hooks/use-toast'

import { useSendReferralToUser } from '../../api/referral-shares/use-send-referral-to-user'
import { useCreateReferral } from '../../api/referrals/use-create-referral'
import { useUpdateReferral } from '../../api/referrals/use-update-referral'

import { ClientInfoForm } from './form-steps/client-info'
import { ConfirmInfo } from './form-steps/confirm-info'
import { PropertyInfo } from './form-steps/property-info'
import ReferralConfirmation from './form-steps/referral-confirmation'
import { AppraiserInfo } from './form-steps/referral-info/appraiser'
import { AttorneyInfo } from './form-steps/referral-info/attorney'
import { ContractorInfo } from './form-steps/referral-info/contractor'
import { InsuranceBrokerInfo } from './form-steps/referral-info/insurance-broker'
import { LoanOfficerInfo } from './form-steps/referral-info/loan-officer'
import { PropertyInspectorInfo } from './form-steps/referral-info/property-inspector'
import { RealEstateAgentInfo } from './form-steps/referral-info/real-estate-agent'
import { TitleRepInfo } from './form-steps/referral-info/title-rep'
import { SelectReferral } from './form-steps/select-referral'

export const SendReferralContent = () => {
  const { data: userData } = useMyUserData()

  const receiver = useSendReferralStore((state) => state.receiver)
  const currentSendReferralStep = useSendReferralStore((state) => state.currentSendReferralStep)
  const error = useSendReferralStore((state) => state.error)
  const setError = useSendReferralStore((state) => state.setError)
  const selectedReferral = useSendReferralStore((state) => state.selectedReferral)
  const referralFormData = useSendReferralStore((state) => state.referralFormData)
  const setSelectedReferral = useSendReferralStore((state) => state.setSelectedReferral)
  const getReferralMode = useSendReferralStore((state) => state.getReferralMode)
  const setCurrentSendReferralStep = useSendReferralStore((state) => state.setCurrentSendReferralStep)

  const addPoints = useAddPoints()
  const { toast } = useToast()
  const { showPointsToast } = usePointsToast()

  const createReferral = useCreateReferral()
  const updateReferral = useUpdateReferral()
  const sendReferralToUser = useSendReferralToUser()

  React.useEffect(() => {
    if (error) {
      toast({
        variant: 'destructive',
        description: error,
      })
    }
  }, [error, toast])

  const handleReferralSuccess = () => {
    if (userData?.id && !userData.hasSentReferral) {
      addPoints.mutate({
        user_id: userData.id,
        amount: 15,
        reason: 'first_sent_referral',
      })
      showPointsToast({
        points: 15,
        message: (
          <div className="flex items-center gap-1">
            You earned {15} <PointIcon className="size-4" /> for sending your first referral!
          </div>
        ),
      })
    }
  }

  const handleSendReferral = async () => {
    try {
      setError(null)
      let referral = selectedReferral
      let newReferral
      let updatedReferral

      switch (getReferralMode()) {
        case 'new':
          newReferral = await createReferral.mutateAsync(referralFormData)
          if (!newReferral) throw new Error('Error creating referral')
          referral = newReferral
          setSelectedReferral(newReferral)
          break
        case 'newProfession':
        case 'repeatProfession':
          if (!selectedReferral) throw new Error('Existing referral not found')
          updatedReferral = await updateReferral.mutateAsync({
            referralId: selectedReferral.id,
            referralData: referralFormData,
          })
          if (!updatedReferral) throw new Error('Error updating referral')
          referral = updatedReferral
          setSelectedReferral(updatedReferral)
          break
      }

      if (!receiver) throw new Error('Receiver not found')
      if (!referral) throw new Error('Referral not found')

      await sendReferralToUser.mutateAsync({
        referralId: referral.id,
        receiverId: receiver.id,
      })

      setCurrentSendReferralStep(currentSendReferralStep + 1)
      handleReferralSuccess()
    } catch (error) {
      console.error(error)
      if (error instanceof Error) {
        setError(error.message)
      } else if (typeof error === 'string') {
        setError(error)
      } else {
        setError('An unknown error occurred')
      }
    }
  }

  const renderStep = () => {
    switch (currentSendReferralStep) {
      case 0:
        return <SelectReferral />
      case 1:
        return <ClientInfoForm />
      case 2:
        return <PropertyInfo />
      case 3:
        switch (receiver?.profession) {
          case 'real_estate_agent':
            return <RealEstateAgentInfo />
          case 'mortgage_loan_officer':
            return <LoanOfficerInfo />
          case 'real_estate_insurance_broker':
            return <InsuranceBrokerInfo />
          case 'contractor':
            return <ContractorInfo />
          case 'real_estate_attorney':
            return <AttorneyInfo />
          case 'property_inspector':
            return <PropertyInspectorInfo />
          case 'real_estate_appraiser':
            return <AppraiserInfo />
          case 'title_company_rep':
            return <TitleRepInfo />
          default:
            return null
        }
      case 4:
        return <ConfirmInfo onSendReferral={handleSendReferral} />
      case 5:
        return <ReferralConfirmation />
      default:
        return <PropertyInfo />
    }
  }

  return (
    <>
      <SendReferralTutorial />
      <div className="space-y-4">
        <ErrorBoundaryWrapper
          title="Sorry, there was an error sending your referral"
          description="Please try again later"
        >
          {renderStep()}
        </ErrorBoundaryWrapper>
      </div>
    </>
  )
}
